import { useMemo } from 'react';
import { uniq } from 'lodash';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { isDefined } from '@e-socna/utils';
import type { ResolverId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { withDisplayedSections } from '../utils/displayed-sections.util';
import {
  collectConditionResolverIds,
  isDisplayedBy,
} from '../utils/section-display-condition.util';
import type { EnrichedSection } from '../types/enriched-section.type';

import { useRequiredResolvers } from './useRequiredResolvers.hook';

// The rendered document: the sections whose display condition is false are
// left out (nor numbered, nor listed in the table of contents).
export const useDisplayedVirtualRoot = (
  virtualRoot: Readonly<EnrichedSection>,
): EnrichedSection => {
  const { resolversIdMap } = useReferencesContext();
  const { allResolvers, setRequiredCommands } = useRequiredResolvers({
    enabled: true,
  });

  const commandOf = useMemo(
    () => (resolverId: ResolverId) => resolversIdMap[resolverId]?.command,
    [resolversIdMap],
  );

  const conditionCommands = useMemo(
    () =>
      uniq(
        collectConditionResolverIds(virtualRoot)
          .map(commandOf)
          .filter(isDefined),
      ),
    [virtualRoot, commandOf],
  );

  useDeepCompareEffect(() => {
    setRequiredCommands(conditionCommands);
  }, [conditionCommands, setRequiredCommands]);

  return useMemo(
    () =>
      withDisplayedSections(virtualRoot, ({ displayCondition }) =>
        isDisplayedBy(displayCondition, (resolverId) => {
          const command = commandOf(resolverId);

          return command ? allResolvers[command] : undefined;
        }),
      ),
    [virtualRoot, commandOf, allResolvers],
  );
};
